import { frequencyStatusCompact, FREQ_DISPONIBLES } from '@/mocks/frequencyCounters';

interface FrequencyStatusBannerProps {
  variant?: 'default' | 'compact';
  className?: string;
}

export default function FrequencyStatusBanner({ variant = 'default', className = '' }: FrequencyStatusBannerProps) {
  const isCompact = variant === 'compact';

  return (
    <div className={`${className}`}>
      {isCompact ? (
        /* ─── COMPACT: single-row horizontal strip ─── */
        <div className="bg-background-50 border border-background-200 rounded-2xl px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {/* Left: urgency counter + message */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center gap-2 shrink-0">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              <span className="text-foreground-950 font-extrabold text-sm tabular-nums whitespace-nowrap">
                {FREQ_DISPONIBLES.toLocaleString('es-ES')}
              </span>
              <span className="text-foreground-400 text-[11px] font-medium whitespace-nowrap">
                frecuencias disponibles
              </span>
            </div>
            <span className="hidden sm:block text-foreground-200 text-sm">|</span>
            <div className="hidden sm:flex items-center gap-3 min-w-0">
              {frequencyStatusCompact.map((item) => (
                <div key={item.label} className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full border ${item.border} ${item.bg}`} style={{ backgroundColor: item.label === 'Ocupadas' ? '#ef4444' : item.label === 'Libres' ? '#10b981' : '#f59e0b' }}></span>
                  <span className="text-foreground-500 text-[11px] font-medium">{item.label}</span>
                  <span className="text-foreground-800 text-[11px] font-bold tabular-nums">{item.value.toLocaleString('es-ES')}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: exclusivity message */}
          <div className="flex items-center gap-2 shrink-0">
            <div className="hidden sm:block w-px h-4 bg-background-300"></div>
            <div className="flex items-center gap-1.5 text-foreground-400 text-[10px]">
              <i className="ri-shield-check-line text-amber-500 text-xs"></i>
              <span className="whitespace-nowrap">Acceso sujeto a aprobación. No todas las radios son aceptadas.</span>
            </div>
          </div>
        </div>
      ) : (
        /* ─── DEFAULT: full banner with urgency emphasis ─── */
        <div className="relative overflow-hidden rounded-2xl border border-primary-200 bg-gradient-to-r from-primary-50 via-primary-50/60 to-accent-50">
          {/* Background pulse glow */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-40 h-40 bg-accent-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-primary-500/10 rounded-full blur-3xl"></div>
          </div>

          <div className="relative z-10 px-5 py-4 md:px-6 md:py-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              {/* Left: urgency section */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-50 border border-red-200 text-red-600 text-[10px] font-bold">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                    ETAPA DE LANZAMIENTO
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-600 text-[10px] font-semibold">
                    <i className="ri-vip-crown-line text-[9px]"></i>
                    Exclusividad limitada
                  </span>
                </div>
                <div>
                  <p className="text-foreground-950 font-extrabold text-lg md:text-xl leading-tight">
                    Solo <span className="text-primary-600">{FREQ_DISPONIBLES.toLocaleString('es-ES')}</span> frecuencias en esta etapa
                  </p>
                  <p className="text-foreground-400 text-[11px] md:text-xs mt-0.5">
                    Cada frecuencia es única. Una vez asignada, esa posición queda reservada para siempre.
                  </p>
                </div>
              </div>

              {/* Right: status indicators */}
              <div className="flex items-center gap-4 md:gap-6">
                {frequencyStatusCompact.map((item) => (
                  <div key={item.label} className="flex flex-col items-center gap-1">
                    <div className={`w-9 h-9 rounded-xl ${item.bg} flex items-center justify-center`}>
                      <i className={`${item.icon} ${item.color} text-sm`}></i>
                    </div>
                    <span className="text-foreground-800 text-sm md:text-base font-extrabold tabular-nums leading-none">
                      {item.value.toLocaleString('es-ES')}
                    </span>
                    <span className="text-foreground-400 text-[9px] md:text-[10px] font-medium">{item.label}</span>
                  </div>
                ))}
              </div>

              {/* Exclusivity message */}
              <div className="hidden lg:flex items-center gap-2 shrink-0 pl-4 border-l border-primary-200">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                  <i className="ri-shield-check-line text-amber-600 text-sm"></i>
                </div>
                <div className="max-w-[160px]">
                  <p className="text-foreground-600 text-[10px] font-semibold leading-tight">
                    Acceso sujeto a aprobación
                  </p>
                  <p className="text-foreground-400 text-[9px] leading-tight mt-0.5">
                    No todas las radios son aceptadas.
                  </p>
                </div>
              </div>
            </div>

            {/* Mobile exclusivity message */}
            <div className="lg:hidden mt-3 pt-3 border-t border-primary-200/60 flex items-center gap-2">
              <i className="ri-shield-check-line text-amber-500 text-xs"></i>
              <p className="text-foreground-500 text-[10px] font-medium">
                Acceso sujeto a aprobación. No todas las radios son aceptadas.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}